<template>
  <div class="cart-sidebar relative z-[100]">
    <Overlay :visible="isOpen" @click="closeSidebar" />
    <Drawer
      ref="reviewsSidebarRef"
      class="reviews-sidebar"
      :is-open="isOpen"
      data-test-id="reviews-sidebar"
      @click:close="closeSidebar"
    >
      <template #header>
        <div class="reviews-sidebar__top">
          <div class="reviews-sidebar__header">
            <div
              v-if="currentStep === 'detail' && !isBackButtonHidden"
              class="back"
              @click="onBack"
            >
              <IconBack />
            </div>
            <div class="reviews-sidebar__title-row">
              <span class="label">
                {{
                  reviewType === 'event'
                    ? t('review.title')
                    : t('review.partner.title')
                }}
              </span>
              <span
                v-if="currentStep === 'list' && averageRating"
                class="reviews-sidebar__rating"
              >
                <ReviewRatingStars
                  class="reviews-sidebar__rating-stars"
                  star-classes="h-4"
                  :average-rating="
                    parseFloat(averageRating.toString().replace(',', '.')) || 0
                  "
                />
                <span class="reviews-sidebar__rating-value">
                  {{ t('review.sidebar.rating', { n: averageRating }) }}
                </span>
              </span>
            </div>
          </div>
        </div>
      </template>

      <div class="flex flex-col justify-between flex-1 pb-8">
        <Transition name="fade" mode="out-in">
          <Component
            :is="getCurrentStepComponent()"
            :key="currentStep"
            v-bind="
              currentStep === 'list'
                ? {
                    reviews: sidebarReviews,
                    averageRating,
                    reviewCount,
                    onClick: showDetailReview,
                    showRatingFilters: !!props.eventPermalink,
                    selectedRating,
                    setRatingFilter: props.eventPermalink
                      ? eventReviewsFetch.setRatingFilter
                      : undefined,
                    reviewBreakdown: props.reviewBreakdown,
                  }
                : { review: detailReview }
            "
          />
        </Transition>
      </div>

      <Button
        v-if="detailReview.event_permalink"
        class="button-link"
        @click="eventLinkClick(detailReview.event_permalink)"
      >
        {{ t('review.transactional.openEvent') }}
      </Button>
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import { resolveComponent } from 'vue'
import type { Review } from '~/composables/types/event'
import IconBack from '~/assets/icons/back.svg'

interface Props {
  reviews: Review[]
  isOpen: boolean
  averageRating: string
  reviewCount: number
  selectedReviewId?: string
  isBackButtonHidden?: boolean
  bottomButtonLink?: string
  reviewType: 'event' | 'partner' | 'third-party'
  eventPermalink?: string
  reviewBreakdown?: Record<string, number>
  initialRatingFilter?: number | null
}

const { t } = useI18n()
const localePath = useLocalePath()

const props = withDefaults(defineProps<Props>(), {
  reviews: () => [],
  isOpen: false,
  averageRating: '',
  reviewCount: 0,
  selectedReviewId: undefined,
  isBackButtonHidden: false,
  bottomButtonLink: '',
  reviewType: 'event',
  eventPermalink: undefined,
  reviewBreakdown: undefined,
  initialRatingFilter: undefined,
})

const currentStep = ref<'list' | 'detail'>('list')
const detailReview = ref<Review>({})

const eventPermalinkRef = computed(() => props.eventPermalink)
const eventReviewsFetch = useEventReviewsFetch(
  { limit: 20 },
  props.eventPermalink ? { eventPermalink: eventPermalinkRef } : {}
)

const sidebarReviews = computed(() =>
  props.eventPermalink ? eventReviewsFetch.reviews.value : props.reviews
)

const selectedRating = computed(() => {
  const search = eventReviewsFetch.search.value
  if (!search?.startsWith('rating:')) return null
  const n = Number.parseInt(search.slice(7), 10)
  return Number.isNaN(n) ? null : n
})

const getCurrentStepComponent = () => {
  if (props.selectedReviewId) {
    showDetailReview(props.selectedReviewId)
    return resolveComponent('ReviewSidebarDetail')
  }

  return currentStep.value === 'detail'
    ? resolveComponent('ReviewSidebarDetail')
    : resolveComponent('ReviewSidebarList')
}

const onBack = () => {
  currentStep.value = 'list'
  detailReview.value = {} as Review
  emit('update:selectedReviewId', undefined)
}

const eventLinkClick = (permalink: string) => {
  emit('close')
  const url = localePath(`/e/${permalink}/`)

  navigateTo(url)
}

const emit = defineEmits(['close', 'update:selectedReviewId'])

watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) {
      document.body.classList.add('prevent-scroll')
      if (props.eventPermalink && props.initialRatingFilter != null) {
        eventReviewsFetch.setRatingFilter(props.initialRatingFilter)
      }
    } else {
      document.body.classList.remove('prevent-scroll')
      if (props.eventPermalink) {
        eventReviewsFetch.setRatingFilter(null)
      }
    }
  }
)

const closeSidebar = () => {
  setTimeout(() => {
    currentStep.value = 'list'
    detailReview.value = {} as Review
  }, 500)
  emit('close')
}

const showDetailReview = (id: string) => {
  const idStr = String(id)
  const review =
    sidebarReviews.value.find((r) => String(r.id) === idStr) ??
    props.reviews.find((r) => String(r.id) === idStr)
  if (!review) return

  currentStep.value = 'detail'
  detailReview.value = review
}
</script>

<style lang="postcss" scoped>
.reviews-sidebar {
  @apply w-full md:max-w-[552px] p-0 overflow-y-scroll flex flex-col;

  &__top {
    @apply relative flex gap-2 items-center px-6 py-4 w-full top-0 border-b border-gray-200;
  }

  &__header {
    @apply w-full flex justify-start items-center;
  }

  &__title-row {
    @apply flex items-center gap-2 flex-1 min-w-0;
  }

  &__header:has(.back) &__title-row {
    @apply pl-8;
  }

  .label {
    @apply text-lg font-semibold text-left;
  }

  &__rating {
    @apply flex items-center gap-1.5 shrink-0;
  }

  &__rating-value {
    @apply text-base text-stone-600;
  }

  .back {
    @apply absolute left-2 top-0 bottom-0 my-auto cursor-pointer w-[22px] h-[22px] shrink-0;

    svg {
      @apply w-full h-full;
    }
  }
}

.button-link {
  @apply mt-auto ml-3 mr-3 mb-3;
}
</style>

<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>
